import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AccessMap, Session } from "../../store/session/types";
import { get } from "lodash";
import { styled } from "@mui/system";
import {
  ExpandMore,
  ExpandLess,
  Receipt,
  History as HistoryIcon,
  Settings as SettingsIcon,
  StorefrontOutlined as StorefrontIcon,
  SupervisedUserCircleOutlined as SupervisedUserCircleIcon,
  PeopleOutlined as PeopleIcon,
  CreditCardOutlined as CreditCardIcon,
  PaymentOutlined as PaymentIcon,
  ImportantDevicesOutlined as ImportDevicesIcon,
  CardMembershipOutlined as CardMembershipIcon,
  AccountBalanceOutlined as AccountBalanceIcon,
  ComputerOutlined as ComputerIcon,
  DashboardOutlined as DashboardIcon,
  PictureInPictureSharp,
  AppSettingsAlt,
  Publish,
  AlignHorizontalLeft,
  Work,
  DevicesFold as DevicesIcon,
  BrowserUpdatedOutlined as CSTerminalIcon,
  CreditCardOff
} from "@mui/icons-material";

import {
  Collapse,
  Tooltip,
  List,
  Divider,
  ListItem,
  ListItemIcon,
  ListItemText,
  Box,
} from "@mui/material";

interface Props {
  session: Session | null;
  access: AccessMap | null;
}

interface ListItemsProps {
  openSettings: boolean;
  setOpenSettings: (value: boolean) => void;
  access: any;
}

interface MenuItem {
  link: string;
  title: string;
  icon?: JSX.Element;
  isChild?: boolean;
}

const classes = {
  txt: { color: "var(--dmg-primary)", textDecoration: "none", fontWeight: "bold" },
  icon: { color: "var(--dmg-primary)" },
  isChild: {
    paddingLeft: 2,
  },
  activeMenuItem: {
    backgroundColor: "rgba(0, 0, 0, 0.08) !important",
  },
};

const StyledLink = styled(Link)({
  color: "var(--dmg-primary)",
  textDecoration: "none",
  fontWeight: "bold",
});

export const MenuItem = ({ link, title, icon, isChild }: MenuItem) => {
  const location = useLocation();
  const isActive = location.pathname === link;

  return (
    <StyledLink to={link}>
      <ListItem sx={isActive ? classes.activeMenuItem : {}}>
        {icon ? (
          <ListItemIcon sx={isChild ? classes.isChild : {}}>
            {icon}
          </ListItemIcon>
        ) : null}

        <ListItemText
          primary={title}
          sx={isChild ? classes.isChild : classes.txt}
          disableTypography={true}
        />
      </ListItem>
    </StyledLink>
  );
};


const MainListItems = ({
  openSettings,
  setOpenSettings,
  access
}: ListItemsProps) => {
  return (
    <div>
      <MenuItem
        title="Dashboard"
        link="/dashboard"
        icon={<DashboardIcon sx={classes.icon} />}
      />
      {get(access, "Merchant.Access.R") && (
        <MenuItem
          title="Merchants"
          link="/merchant"
          icon={<StorefrontIcon sx={classes.icon} />}
        />
      )}
      {get(access, "Terminal.Access.R") && (
        <MenuItem
          title="Terminals"
          link="/terminal"
          icon={<ImportDevicesIcon sx={classes.icon} />}
        />
      )}
      {get(access, "Onboarding.Access.C") && (
        <MenuItem
          title="Onboarding"
          link="/onboarding"
          icon={<Publish sx={classes.icon} />}
        />
      )}
      {get(access, "InjectedTerminal.Access.R") && (
        <MenuItem
          title="Injected Terminals"
          link="/injected-terminal"
          icon={<AppSettingsAlt sx={classes.icon} />}
        />
      )}
      {get(access, "StageMerchant.Access.R") && (
        <MenuItem
          title="Staged Terminals"
          link="/staged-terminal"
          icon={<StorefrontIcon sx={classes.icon} />}
        />
      )}
      {get(access, "POS.Access.R") && (
        <MenuItem
          title="POS"
          link="/pos"
          icon={<PaymentIcon sx={classes.icon} />}
        />
      )}
      {get(access, "POSSoftware.Access.R") && (
        <MenuItem
          title="POS Software"
          link="/possoftware"
          icon={<ComputerIcon sx={classes.icon} />}
        />
      )}
      {get(access, "Processor.Access.R") && (
        <MenuItem
          title="Processors"
          link="/processor"
          icon={<AccountBalanceIcon sx={classes.icon} />}
        />
      )}
      {get(access, "Card.Access.R") && (
        <MenuItem
          title="Card Prefix"
          link="/card"
          icon={<CreditCardIcon sx={classes.icon} />}
        />
      )}
      {get(access, "CardPrompts.Access.R") && (
        <MenuItem
          title="Card Prompts"
          link="/cardPrompt"
          icon={<CreditCardIcon sx={classes.icon} />}
        />
      )}
      {get(access, "Receipt.Access.R") && (
        <MenuItem
          title="Receipts"
          link="/receipt"
          icon={<Receipt sx={classes.icon} />}
        />
      )}
      {get(access, "TerminalBackground.Access.R") && (
        <MenuItem
          title="Background"
          link="/terminal-background"
          icon={<PictureInPictureSharp sx={classes.icon} />}
        />
      )}
      {get(access, "HashedHotCard.Access.R") && (
        <MenuItem
          title="HotCards"
          link="/hotcard"
          icon={<CreditCardOff sx={classes.icon} />}
        />
      )}
      {(get(access, "User.Access.R") ||
        get(access, "UserGroup.Access.R") ||
        get(access, "AuditTrail.Access.R") ||
        get(access, "CardType.Access.R")) && (
        <React.Fragment>
          <ListItem
            onClick={() => setOpenSettings(!openSettings)}
            sx={classes.txt}
          >
            <ListItemIcon>
              <SettingsIcon sx={classes.icon} />
            </ListItemIcon>
            <ListItemText
              primary="Settings"
              sx={classes.txt}
              disableTypography={true}
            />
            {openSettings ? <ExpandLess /> : <ExpandMore />}
          </ListItem>
          <Collapse in={openSettings} timeout="auto" unmountOnExit>
            <Divider />
            <List component="div" disablePadding>
              {get(access, "User.Access.R") && (
                <MenuItem
                  title="Users"
                  link="/user"
                  isChild
                  icon={<SupervisedUserCircleIcon sx={classes.icon} />}
                />
              )}
              {get(access, "UserGroup.Access.R") && (
                <MenuItem
                  title="Roles"
                  link="/role"
                  isChild
                  icon={<PeopleIcon sx={classes.icon} />}
                />
              )}
              {get(access, "AuditTrail.Access.R") && (
                <MenuItem
                  title="Audit Log"
                  link="/audit"
                  isChild
                  icon={<HistoryIcon sx={classes.icon} />}
                />
              )}
              {get(access, "CardType.Access.R") && (
                <MenuItem
                  title="Card Types"
                  link="/cardType"
                  isChild
                  icon={<CreditCardIcon sx={classes.icon} />}
                />
              )}
            </List>
          </Collapse>
        </React.Fragment>
      )}
    </div>
  )
};

export default function Nav({ access }: Props) {
  const location = useLocation();
  const inSettingsMenuPaths = [
    "/user",
    "/role",
    "/audit",
    "/cardType",
  ]; // add links for item in settings sub-menu to make openSettings opened
  const inCSMenuPaths = [
    "/branch",
    "/workstation",
    "/cs-dashboard",
    "/device",
  ]; // add links for item in settings sub-menu to make openSettings opened
  const [openSettings, setOpenSettings] = useState(
    inSettingsMenuPaths.some((path) => location.pathname.startsWith(path)) ||
      false
  );

  return (
    <Box
      sx={{
        overflow: "auto",
      }}
    >
      <Divider />
      <List>
        <MainListItems
          access={access}
          openSettings={openSettings}
          setOpenSettings={setOpenSettings}
        />
      </List>
      <Divider />
      <List>
        <ListItem>
          <Tooltip
            title={
              <div
                dangerouslySetInnerHTML={{
                  __html: (global.VERSION_DETAILS as string) || "",
                }}
              />
            }
          >
            <ListItemText
              primary={global.VERSION || ""}
              sx={classes.txt}
            />
          </Tooltip>
        </ListItem>
      </List>
    </Box>
  );
}
